import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Edit, Trash2, Save, X, ArrowLeft, Pin, PinOff,
  Eye, EyeOff, Bell, BellOff, Calendar, User
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const ClubBlog = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    is_pinned: false,
    is_published: true,
    show_on_login: false
  });

  const isAdmin = user?.is_admin || user?.is_operator_admin || 
                  ['club_officer', 'ngo_officer'].includes(user?.member_class);

  useEffect(() => {
    if (postId) {
      fetchPost(postId);
    } else {
      fetchPosts();
    }
  }, [postId]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/club/blog`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPost = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/club/blog/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPost(data);
      } else {
        navigate('/club-blog');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/club-blog');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (post = null) => {
    if (post && post.id !== 'placeholder') {
      setEditingPost(post);
      setForm({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt || '',
        is_pinned: post.is_pinned,
        is_published: post.is_published,
        show_on_login: post.show_on_login
      });
    } else {
      setEditingPost(null);
      setForm({
        title: '',
        content: '',
        excerpt: '',
        is_pinned: false,
        is_published: true,
        show_on_login: false
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingPost(null);
  };

  const savePost = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingPost 
        ? `${API}/api/club/blog/${editingPost.id}`
        : `${API}/api/club/blog`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        closeEditor();
        if (postId) {
          fetchPost(postId);
        } else {
          fetchPosts();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const res = await fetch(`${API}/api/club/blog/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Post deleted');
        if (postId) {
          navigate('/club-blog');
        } else {
          fetchPosts();
        }
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Single post view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-8">
          <div className="container mx-auto px-4">
            <button 
              onClick={() => navigate('/club-blog')}
              className="flex items-center gap-2 text-amber-100 hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all posts
            </button>
            <h1 className="text-3xl font-heading font-bold">{selectedPost.title}</h1>
            <div className="flex items-center gap-4 mt-4 text-amber-100">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {selectedPost.author_name}
                {selectedPost.author_title && ` — ${selectedPost.author_title}`}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(selectedPost.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {isAdmin && selectedPost.id !== 'placeholder' && (
            <div className="flex gap-2 mb-6">
              <Button variant="outline" onClick={() => openEditor(selectedPost)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" className="text-red-600" onClick={() => deletePost(selectedPost.id)}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}

          <Card>
            <CardContent className="pt-6">
              <div className="prose prose-amber max-w-none">
                <ReactMarkdown>{selectedPost.content}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Editor Modal */}
        {showEditor && <EditorModal />}
      </div>
    );
  }

  // Post list view
  const EditorModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold">
            {editingPost ? 'Edit Post' : 'New Club Post'}
          </h2>
          <button onClick={closeEditor} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="Post title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Excerpt (optional)</label>
            <Input
              value={form.excerpt}
              onChange={(e) => setForm({...form, excerpt: e.target.value})}
              placeholder="Brief summary for previews"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Content * (Markdown supported)</label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({...form, content: e.target.value})}
              rows={15}
              className="font-mono text-sm"
              placeholder="Write your post content here..."
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({...form, is_published: e.target.checked})}
                className="w-4 h-4"
              />
              <Eye className="w-4 h-4" />
              <span className="text-sm">Published</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_pinned}
                onChange={(e) => setForm({...form, is_pinned: e.target.checked})}
                className="w-4 h-4"
              />
              <Pin className="w-4 h-4" />
              <span className="text-sm">Pinned</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.show_on_login}
                onChange={(e) => setForm({...form, show_on_login: e.target.checked})}
                className="w-4 h-4"
              />
              <Bell className="w-4 h-4" />
              <span className="text-sm">Show on Dashboard</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <Button variant="outline" onClick={closeEditor}>Cancel</Button>
          <Button onClick={savePost} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Post'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-heading font-bold mb-2">Club News & Updates</h1>
          <p className="text-amber-100 text-lg">
            Messages and announcements from All American Dog Club leadership
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {isAdmin && (
          <div className="mb-6">
            <Button onClick={() => openEditor()} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No posts yet</p>
            {isAdmin && (
              <Button onClick={() => openEditor()} className="mt-4">
                Create First Post
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <Card 
                key={post.id} 
                className={`cursor-pointer hover:shadow-lg transition ${post.is_pinned ? 'border-amber-300 bg-amber-50/50' : ''}`}
                onClick={() => navigate(`/club-blog/${post.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {post.is_pinned && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                            <Pin className="w-3 h-3" /> Pinned
                          </span>
                        )}
                        {post.show_on_login && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                            <Bell className="w-3 h-3" /> Featured
                          </span>
                        )}
                        {post.id === 'placeholder' && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            Sample Post
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl">{post.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    {post.excerpt || post.content.substring(0, 200)}...
                  </p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {post.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && <EditorModal />}
    </div>
  );
};

export default ClubBlog;
